'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';

export default function HostingGuidelinesPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/hosting">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Hosting
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Kawkb Hosting Guidelines</h1>
          <div className="flex items-start gap-2 mt-4 max-w-3xl bg-amber-50 p-4 rounded-lg border border-amber-200">
            <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-lg text-amber-800">
              Stay Updated! For the latest hosting tips and best practices, follow us on social
              media. We share insights and real-life stories from fellow hosts to help you succeed!
            </p>
          </div>
        </div>
      </section>

      {/* Guidelines Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  1
                </span>
                Property Requirements
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    Hosting on Kawkb is exclusively for properties located within gated communities.
                    This requirement is non-negotiable to maintain the quality and security expected
                    by our guests.
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    For coastal area properties, hosts must facilitate guest access to the rental
                    beach area and rental pool area. Ensure guests have the necessary permissions or
                    passes to enjoy all available amenities within the compound.
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    Ensure your property is fully furnished with essential appliances to be eligible
                    for listing.
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Maintain cleanliness and good condition of the space at all times.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Comply with local safety regulations (e.g., smoke detectors, secure locks).</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  2
                </span>
                Setting Up Your Space
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Create a welcoming atmosphere with clean, comfortable furnishings.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Provide essential amenities (fresh linens, toiletries, basic kitchenware).</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Make sure the space is well-lit, ventilated, and organized.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  3
                </span>
                Communicating with Guests
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Respond to inquiries promptly through the Control Hub.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    Clearly outline check-in, check-out, and house rules before the guest arrives.
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    Be polite, professional, and ready to assist if issues arise during the stay.
                  </p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  4
                </span>
                Managing Bookings and Payments
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Regularly update your availability and pricing in the Control Hub.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Confirm bookings as soon as possible to avoid cancellations.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Be transparent about fees and charges.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  5
                </span>
                Maintaining Your Property
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Clean and sanitize between stays to ensure a pleasant guest experience.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Fix any maintenance issues before hosting new guests.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Keep safety equipment (like fire extinguishers) in working order.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  6
                </span>
                Handling Issues or Complaints
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Stay calm and professional when addressing guest complaints.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Use the Control Hub to document and report any issues.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Contact Kawkb support for assistance when needed.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  7
                </span>
                Respecting the Community
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Be mindful of noise levels, parking rules, and shared spaces.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Encourage guests to respect the community and its regulations.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  8
                </span>
                Staying Compliant
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Adhere to local laws regarding short-term rentals and tax obligations.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Keep necessary permits up to date.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Report your earnings as required by local authorities.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  9
                </span>
                Updating Your Listing
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Keep property photos and descriptions accurate and up to date.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Highlight any changes or improvements to attract more bookings.</p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Include house rules and special instructions clearly.</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-lg font-bold mr-3">
                  10
                </span>
                Get Support When Needed
              </h2>
              <ul className="list-none space-y-6">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>
                    Reach out to Kawkb's local support team for hosting assistance or technical
                    issues.
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                  <p>Stay connected through our social media for updates and community insights.</p>
                </li>
              </ul>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl border border-primary/10 text-center mt-12">
              <h2 className="text-2xl font-bold mb-2">Happy Hosting with Kawkb!</h2>
              <p className="text-lg">
                Follow these guidelines to provide exceptional experiences for your guests.
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
